import React from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Row,
    Col,
    FormGroup,
    Label,
    FormFeedback,
    UncontrolledTooltip,
} from 'reactstrap';
import moment from 'moment';
import { faTasks } from '@fortawesome/free-solid-svg-icons';
import { email as emailUtil } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../../Common';
import {
    AppPageForm,
    FormLabel,
    Input,
    onFieldChange,
    toasty,
} from '../../common/forms/FormElements';
import { Radio, RadioGroup } from '../../common/forms/Radio';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import EmailRecipients from '../../common/forms/EmailRecipients';
import './index.scss';

class HandoffEmail {
    dispatchLocationId = 0;
    handoffEmailRecipients = '';
}

export class DispatchPlusHandoff extends React.Component {
    static contextType = CommonContext;
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            saving: false,
            formValidated: false,
            dispatchLocations: [],
            handoffEmail: new HandoffEmail(),
            dispatchLocation: '',
            employeeOptions: [],
            selectedDate: moment().format('YYYY-MM-DD'),
            currentEmailAddress: '',
        };
    }

    componentDidMount() {
        this.populateState();
    }

    populateState = async () => {
        const dispatchLocations = await httpClient.js(ApiRoutes.typeAheads.dispatchCompaniesForDispatchPlus()).catch(this.handleFetchError);
        this.setState({
            loading: false,
            dispatchLocations,
        });
    };

    fetchHandoffData = async (dispatchLocationId, date) => {
        this.setState({ loading: true });
        try {
            const [handoffEmail, employeeOptions] = await Promise.all([
                httpClient.js(ApiRoutes.dispatchPlus.handoff(dispatchLocationId, date)),
                httpClient.js(ApiRoutes.typeAheads.employeesByDispatchLocation(dispatchLocationId)),
            ]);
            if (handoffEmail) {
                this.setState({ handoffEmail, employeeOptions, loading: false });
            }
        } catch (error) {
            this.handleFetchError(error);
        }
    };

    handleFetchError = () => {
        this.setState({ handoffEmail: new HandoffEmail(), loading: false });
        toasty.error('Unable to load handoff data. Please try again.');
    };

    handleDispatchLocationChange = (selected) => {
        const dispatchLocationId = selected?.value ?? '';
        this.setState({ dispatchLocation: dispatchLocationId, handoffEmail: new HandoffEmail() });
        if (dispatchLocationId) {
            this.fetchHandoffData(dispatchLocationId, this.state.selectedDate);
        }
    };

    handleDateChange = (e) => {
        const selectedDate = e.target.value;
        this.setState({ selectedDate });
        const { dispatchLocation } = this.state;
        if (dispatchLocation) {
            this.fetchHandoffData(dispatchLocation, selectedDate);
        }
    };

    onChange = onFieldChange.bind(this);

    handleShiftEmployeesChange = (shiftTypeId, selectedEmployees) => {
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                employeesByShift: {
                    ...prevState.handoffEmail.employeesByShift,
                    [shiftTypeId]: selectedEmployees || [],
                },
            },
        }));
    };

    handleDailyAttendanceEventChange = (eventId, selectedEmployees) => {
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                employeesByDailyAttendanceEvent: {
                    ...prevState.handoffEmail.employeesByDailyAttendanceEvent,
                    [eventId]: selectedEmployees || [],
                },
            },
        }));
    };

    handleLongTermStatusChange = (statusId, selectedEmployees) => {
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                employeesByLongTermStatus: {
                    ...prevState.handoffEmail.employeesByLongTermStatus,
                    [statusId]: selectedEmployees || [],
                },
            },
        }));
    };

    handleJobAssignmentCancellationChange = (cancellationTypeId, selectedEmployees) => {
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                employeesByJobAssignmentCancellation: {
                    ...prevState.handoffEmail.employeesByJobAssignmentCancellation,
                    [cancellationTypeId]: selectedEmployees || [],
                },
            },
        }));
    };

    handleEmailChanged = (e) => {
        this.setState({ currentEmailAddress: e.target.value });
    };

    handleEmailAdded = () => {
        const { currentEmailAddress, handoffEmail } = this.state;

        if (currentEmailAddress) {
            const emailAddress = currentEmailAddress.trim();
            const match = emailUtil(emailAddress);
            if (match) {
                const currentRecipients = typeof handoffEmail.additionalRecipients === 'string'
                    ? handoffEmail.additionalRecipients.split(',').filter(Boolean)
                    : (handoffEmail.additionalRecipients || []);

                if (!currentRecipients.includes(emailAddress)) {
                    const newRecipients = [...currentRecipients, emailAddress];
                    this.setState({
                        handoffEmail: {
                            ...handoffEmail,
                            additionalRecipients: newRecipients.join(','),
                        },
                        currentEmailAddress: '',
                    });
                }
            } else {
                toasty.error('Please enter a single valid email address.');
            }
        }
    };

    handleEmailRemoved = (email) => {
        this.setState((prevState) => {
            const currentRecipients = typeof prevState.handoffEmail.additionalRecipients === 'string'
                ? prevState.handoffEmail.additionalRecipients.split(',').filter(Boolean)
                : (prevState.handoffEmail.additionalRecipients || []);

            const newRecipients = currentRecipients.filter((e) => e !== email);
            return {
                handoffEmail: {
                    ...prevState.handoffEmail,
                    additionalRecipients: newRecipients.join(','),
                },
            };
        });
    };

    handleAllCrewConfirmedChange = (e) => {
        const value = e.target.value === 'true';
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                allConfirmed: value,
            },
        }));
    };

    handleNotConfirmedChange = (selectedEmployees) => {
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                notConfirmed: selectedEmployees || [],
            },
        }));
    };

    handleMiscRadioChange = (fieldName) => (e) => {
        const isYes = e.target.value === 'true';
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                [fieldName]: isYes ? prevState.handoffEmail[fieldName] || '' : null,
            },
            [`${fieldName}Selection`]: isYes ? 'yes' : 'no',
        }));
    };

    handleMiscInputChange = (fieldName) => (e) => {
        const { value } = e.target;
        this.setState((prevState) => ({
            handoffEmail: {
                ...prevState.handoffEmail,
                [fieldName]: value,
            },
        }));
    };

    jobActivityFields = [
        { field: 'calledInJobs', label: 'Called in Jobs', placeholder: 'Any last minute or called-in jobs' },
        { field: 'missedOpportunities', label: 'Missed Opportunities', placeholder: 'Any missed opportunities' },
    ];

    miscFields = [
        { field: 'missedJobs', label: 'Missed Jobs', placeholder: 'Any missed jobs' },
        { field: 'wrongLocation', label: 'Wrong Location', placeholder: 'Wrong location details' },
        { field: 'multipleCrewsToSameJobSite', label: 'Multiple Crews to Same Job Site', placeholder: 'Multiple crews details' },
        { field: 'wrongTimeOnRoads', label: 'Wrong Time on Roads', placeholder: 'Wrong time details' },
        { field: 'jobNotCancelled', label: 'Job Not Cancelled', placeholder: 'Job not cancelled details' },
        { field: 'salesIssues', label: 'Sales Issues', placeholder: 'Sales issues details' },
        { field: 'miscellaneous', label: 'Miscellaneous', placeholder: 'Any other notes' },
    ];

    isFormValid = () => {
        const { handoffEmail } = this.state;

        // Check dispatch location
        if (!handoffEmail.dispatchLocationId) return false;

        // Check allConfirmed is selected
        if (handoffEmail.allConfirmed === null || handoffEmail.allConfirmed === undefined) return false;

        // Check all job activity fields are selected (yes or no)
        for (const { field } of this.jobActivityFields) {
            const selection = this.state[`${field}Selection`];
            if (selection !== 'yes' && selection !== 'no') return false;
        }

        // Check all misc fields are selected (yes or no)
        for (const { field } of this.miscFields) {
            const selection = this.state[`${field}Selection`];
            if (selection !== 'yes' && selection !== 'no') return false;
        }

        return true;
    };

    handleSubmit = async () => {
        const { handoffEmail } = this.state;

        this.setState({ formValidated: true });

        if (!this.isFormValid()) {
            toasty.error('Please fill in all required fields.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.dispatchPlus.submitHandoff(), handoffEmail);
            toasty.success('Handoff email sent successfully.');
        } catch {
            toasty.error('Failed to send handoff email. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            loading,
            saving,
            dispatchLocations,
            dispatchLocation,
            handoffEmail,
            employeeOptions,
            currentEmailAddress,
        } = this.state;
        const additionalRecipients = typeof handoffEmail.additionalRecipients === 'string'
            ? handoffEmail.additionalRecipients.split(',').filter(Boolean)
            : [];
        const errors = [];
        const isDisabled = loading;
        const handoffDataLoaded = !!handoffEmail.dispatchLocationId;

        return (
            <AppPageForm
                formId="dispatchPlusForm"
                formName="dispatchPlusForm"
                formHeadingIcon={faTasks}
                formHeading="DispatchPlus"
                onSubmit={this.handleSubmit}
                setIsValidated={(value) => this.setState({ formValidated: value })}
                loading={loading}
                saving={saving}
                errors={errors}
                backUrl={AppNavPaths.Dashboard}
            >
                <Row>
                    <Col xs={12}>
                        <Card className="mb-3">
                            <CardHeader className="d-flex flex-row justify-content-between align-items-center">
                                <strong>Handoff Details</strong>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md={6}>
                                        <ValidatedSelect
                                            id="dispatchLocation"
                                            name="dispatchLocation"
                                            label="Dispatch Location"
                                            required
                                            isDisabled={isDisabled}
                                            options={dispatchLocations}
                                            value={
                                                dispatchLocations.find((loc) => loc.value === dispatchLocation)
                                                ?? ''
                                            }
                                            onChange={this.handleDispatchLocationChange}
                                            error="A dispatch location is required."
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted">Written By</Label>
                                            <div>{this.context.user.fullName}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Input
                                            type="date"
                                            id="selectedDate"
                                            name="selectedDate"
                                            label="Date"
                                            disabled={isDisabled}
                                            value={this.state.selectedDate}
                                            onChange={this.handleDateChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted">Recipients</Label>
                                            {!dispatchLocation && (
                                                <div className="text-muted">Select a dispatch location</div>
                                            )}
                                            {dispatchLocation && handoffEmail.handoffEmailRecipients && (
                                                <div>
                                                    {handoffEmail.handoffEmailRecipients}
                                                </div>
                                            )}
                                            {dispatchLocation && !handoffEmail.handoffEmailRecipients && (
                                                <div className="text-muted">No recipients found</div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="email"
                                                label="Additional Recipients"
                                            />
                                            <EmailRecipients
                                                emailAddresses={additionalRecipients}
                                                currentEmailAddress={currentEmailAddress}
                                                onEmailChanged={this.handleEmailChanged}
                                                onEmailAdded={this.handleEmailAdded}
                                                onEmailRemoved={this.handleEmailRemoved}
                                                readOnly={isDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {handoffDataLoaded && (
                            <>
                                <Card className="mb-3">
                                    <CardHeader>
                                        <strong>Crews Confirmed</strong>
                                    </CardHeader>
                                    <CardBody>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="allConfirmed"
                                                label="Are all crews confirmed?"
                                                required
                                            />
                                            <RadioGroup
                                                name="allConfirmed"
                                                value={handoffEmail.allConfirmed === null ? null : String(handoffEmail.allConfirmed)}
                                                disabled={isDisabled}
                                                onChange={this.handleAllCrewConfirmedChange}
                                            >
                                                <div className="d-flex">
                                                    <Radio
                                                        value="true"
                                                        label="Yes"
                                                        className="mr-4 radio-no-border"
                                                    />
                                                    <Radio
                                                        value="false"
                                                        label="No"
                                                        className="radio-no-border"
                                                    />
                                                </div>
                                            </RadioGroup>
                                            <input
                                                type="hidden"
                                                required
                                                value={handoffEmail.allConfirmed ?? ''}
                                            />
                                            <FormFeedback className={this.state.formValidated && handoffEmail.allConfirmed === null ? 'd-block' : ''}>
                                                Please select Yes or No.
                                            </FormFeedback>
                                        </FormGroup>
                                        {!handoffEmail.allConfirmed && (
                                            <ValidatedSelect
                                                id="notConfirmed"
                                                name="notConfirmed"
                                                label={`Not Confirmed (${handoffEmail.notConfirmed?.length ?? 0})`}
                                                isMulti
                                                isDisabled={isDisabled}
                                                options={employeeOptions}
                                                value={handoffEmail.notConfirmed || []}
                                                onChange={this.handleNotConfirmedChange}
                                                placeholder="Select employees..."
                                            />
                                        )}
                                    </CardBody>
                                </Card>

                                {(handoffEmail.shiftTypes || []).length > 0 && (
                                    <Card className="mb-3">
                                        <CardHeader>
                                            <strong>Operational</strong>
                                        </CardHeader>
                                        <CardBody>
                                            {(handoffEmail.shiftTypes || []).map(({ value: shiftTypeId, label }) => {
                                                const options = employeeOptions;
                                                const selectedEmployees = handoffEmail.employeesByShift?.[shiftTypeId] || [];

                                                const customOptionLabel = (option) => {
                                                    const statusText = option.statusText || option.status;
                                                    return (
                                                        <>
                                                            <span id={`emp-option-${shiftTypeId}-${option.value}`}>
                                                                {option.label}
                                                            </span>
                                                            {statusText && (
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`emp-option-${shiftTypeId}-${option.value}`}
                                                                >
                                                                    {statusText}
                                                                </UncontrolledTooltip>
                                                            )}
                                                        </>
                                                    );
                                                };

                                                return (
                                                    <ValidatedSelect
                                                        id={`onCallSpares_${shiftTypeId}`}
                                                        name={`onCallSpares_${shiftTypeId}`}
                                                        key={shiftTypeId}
                                                        label={`${label} (${selectedEmployees?.length ?? 0})`}
                                                        isMulti
                                                        isDisabled={isDisabled}
                                                        options={options}
                                                        value={selectedEmployees}
                                                        onChange={(selected) => this.handleShiftEmployeesChange(shiftTypeId, selected)}
                                                        placeholder="Select employees..."
                                                        formatOptionLabel={customOptionLabel}
                                                    />
                                                );
                                            })}
                                        </CardBody>
                                    </Card>
                                )}

                                <Card className="mb-3">
                                    <CardHeader>
                                        <strong>Employee Status</strong>
                                    </CardHeader>
                                    <CardBody>
                                        {(handoffEmail.jobCancellationTypes || []).map(({ value: cancellationTypeId, label }) => {
                                            const selectedEmployees = handoffEmail.employeesByJobAssignmentCancellation?.[cancellationTypeId] || [];
                                            return (
                                                <ValidatedSelect
                                                    id={`jobCancellationType_${cancellationTypeId}`}
                                                    name={`jobCancellationType_${cancellationTypeId}`}
                                                    label={`${label} (${selectedEmployees?.length ?? 0})`}
                                                    key={`cancellation_${cancellationTypeId}`}
                                                    isMulti
                                                    isDisabled={isDisabled}
                                                    options={employeeOptions}
                                                    value={selectedEmployees}
                                                    onChange={(selected) => this.handleJobAssignmentCancellationChange(cancellationTypeId, selected)}
                                                    placeholder="Select employees..."
                                                />
                                            );
                                        })}

                                        {(handoffEmail.dailyAttendanceEvents || []).map(({ value: eventId, label }) => {
                                            const selectedEmployees = handoffEmail.employeesByDailyAttendanceEvent?.[eventId] || [];
                                            return (
                                                <ValidatedSelect
                                                    id={`dailyAttendanceEvent_${eventId}`}
                                                    name={`dailyAttendanceEvent_${eventId}`}
                                                    label={`${label} (${selectedEmployees?.length ?? 0})`}
                                                    key={`event_${eventId}`}
                                                    isMulti
                                                    isDisabled={isDisabled}
                                                    options={employeeOptions}
                                                    value={selectedEmployees}
                                                    onChange={(selected) => this.handleDailyAttendanceEventChange(eventId, selected)}
                                                    placeholder="Select employees..."
                                                />
                                            );
                                        })}
                                    </CardBody>
                                </Card>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <strong>Staffing</strong>
                                    </CardHeader>
                                    <CardBody>
                                        {(handoffEmail.longTermStatuses || []).map(({ value: statusId, label }) => {
                                            const selectedEmployees = handoffEmail.employeesByLongTermStatus?.[statusId] || [];
                                            return (
                                                <ValidatedSelect
                                                    id={`longTermStatus_${statusId}`}
                                                    name={`longTermStatus_${statusId}`}
                                                    key={statusId}
                                                    label={`${label} (${selectedEmployees?.length ?? 0})`}
                                                    isMulti
                                                    isDisabled={isDisabled}
                                                    options={employeeOptions}
                                                    value={selectedEmployees}
                                                    onChange={(selected) => this.handleLongTermStatusChange(statusId, selected)}
                                                    placeholder="Select employees..."
                                                />
                                            );
                                        })}
                                    </CardBody>
                                </Card>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <strong>Job Activity</strong>
                                    </CardHeader>
                                    <CardBody>
                                        <Input
                                            type="number"
                                            id="numberOfJobs"
                                            label="Number of Jobs"
                                            name="handoffEmail.numberOfJobs"
                                            min={0}
                                            disabled={isDisabled}
                                            value={handoffEmail.numberOfJobs ?? ''}
                                            onChange={this.onChange}
                                        />

                                        {this.jobActivityFields.map(({ field, label, placeholder }) => {
                                            const selection = this.state[`${field}Selection`];
                                            const isYes = selection === 'yes';
                                            const isNo = selection === 'no';
                                            const isNotSelected = !isYes && !isNo;
                                            let radioValue = '';
                                            if (isYes) radioValue = 'true';
                                            else if (isNo) radioValue = 'false';
                                            return (
                                                <FormGroup key={field}>
                                                    <FormLabel
                                                        htmlFor={field}
                                                        text={label}
                                                        required
                                                    />
                                                    <Row>
                                                        <Col md={3}>
                                                            <RadioGroup
                                                                name={field}
                                                                value={radioValue}
                                                                disabled={isDisabled}
                                                                onChange={this.handleMiscRadioChange(field)}
                                                            >
                                                                <div className="d-flex">
                                                                    <Radio
                                                                        value="true"
                                                                        label="Yes"
                                                                        className="mr-4 radio-no-border"
                                                                    />
                                                                    <Radio
                                                                        value="false"
                                                                        label="No"
                                                                        className="radio-no-border"
                                                                    />
                                                                </div>
                                                            </RadioGroup>
                                                            <input
                                                                type="hidden"
                                                                required
                                                                value={selection ?? ''}
                                                            />
                                                            <FormFeedback className={this.state.formValidated && isNotSelected ? 'd-block' : ''}>
                                                                Please select Yes or No.
                                                            </FormFeedback>
                                                        </Col>
                                                        <Col md={9}>
                                                            {isYes && (
                                                                <Input
                                                                    type="textarea"
                                                                    id={field}
                                                                    name={`handoffEmail.${field}`}
                                                                    rows={2}
                                                                    placeholder={placeholder}
                                                                    disabled={isDisabled}
                                                                    value={handoffEmail[field] || ''}
                                                                    onChange={this.handleMiscInputChange(field)}
                                                                    required
                                                                    invalid={this.state.formValidated && !handoffEmail[field]}
                                                                    error={this.state.formValidated && !handoffEmail[field] ? 'Please provide details.' : ''}
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            );
                                        })}
                                    </CardBody>
                                </Card>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <strong>Issues</strong>
                                    </CardHeader>
                                    <CardBody>
                                        {this.miscFields.map(({ field, label, placeholder }) => {
                                            const selection = this.state[`${field}Selection`];
                                            const isYes = selection === 'yes';
                                            const isNo = selection === 'no';
                                            const isNotSelected = !isYes && !isNo;
                                            let radioValue = '';
                                            if (isYes) radioValue = 'true';
                                            else if (isNo) radioValue = 'false';
                                            return (
                                                <FormGroup key={field}>
                                                    <FormLabel
                                                        htmlFor={field}
                                                        text={label}
                                                        required
                                                    />
                                                    <Row>
                                                        <Col md={3}>
                                                            <RadioGroup
                                                                name={field}
                                                                value={radioValue}
                                                                disabled={isDisabled}
                                                                onChange={this.handleMiscRadioChange(field)}
                                                            >
                                                                <div className="d-flex">
                                                                    <Radio
                                                                        value="true"
                                                                        label="Yes"
                                                                        className="mr-4 radio-no-border"
                                                                    />
                                                                    <Radio
                                                                        value="false"
                                                                        label="No"
                                                                        className="radio-no-border"
                                                                    />
                                                                </div>
                                                            </RadioGroup>
                                                            <input
                                                                type="hidden"
                                                                required
                                                                value={selection ?? ''}
                                                            />
                                                            <FormFeedback className={this.state.formValidated && isNotSelected ? 'd-block' : ''}>
                                                                Please select Yes or No.
                                                            </FormFeedback>
                                                        </Col>
                                                        <Col md={9}>
                                                            {isYes && (
                                                                <Input
                                                                    type="textarea"
                                                                    id={field}
                                                                    name={`handoffEmail.${field}`}
                                                                    rows={2}
                                                                    placeholder={placeholder}
                                                                    disabled={isDisabled}
                                                                    value={handoffEmail[field] || ''}
                                                                    onChange={this.handleMiscInputChange(field)}
                                                                    required
                                                                    invalid={this.state.formValidated && !handoffEmail[field]}
                                                                    error={this.state.formValidated && !handoffEmail[field] ? 'Please provide details.' : ''}
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            );
                                        })}

                                        <Input
                                            type="textarea"
                                            id="miscComments"
                                            name="handoffEmail.miscComments"
                                            label="Comments"
                                            rows={3}
                                            placeholder="Anything else the next team should know"
                                            disabled={isDisabled}
                                            value={handoffEmail.miscComments || ''}
                                            onChange={this.onChange}
                                        />
                                    </CardBody>
                                </Card>

                                <div className="pb-3">
                                    <Button
                                        color="primary"
                                        block
                                        type="submit"
                                        disabled={isDisabled}
                                    >
                                        SEND HANDOFF EMAIL
                                    </Button>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </AppPageForm>
        );
    }
}
